import type { CellFormat } from '../compositor/GridController'
import type { SpreadsheetConditionalFormatRule, SpreadsheetRangeAddress } from '../protocol/spreadsheetEngineProtocol'
import { formatSelectionAddress, parseA1Selection } from './addressing'

export type ConditionalFormatKind =
  | 'cellIs'
  | 'formula'
  | 'duplicateValues'
  | 'timePeriod'
  | 'topBottom'
  | 'aboveBelowAverage'
  | 'colorScale'
  | 'dataBar'
  | 'iconSet'

export type AuthoredConditionalFormatKind =
  | ConditionalFormatKind
  | 'uniqueValues'
  | 'containsBlanks'
  | 'notContainsBlanks'
  | 'containsErrors'
  | 'notContainsErrors'

export type ConditionalFormatRuleFilter = 'selection' | 'worksheet' | 'workbook'

export type ConditionalFormatOption = {
  value: string
  label: string
}

export type ConditionalFormatRuleTypeOption = {
  id: ConditionalFormatKind
  label: string
}

export type ConditionalFormatPresetStyle = {
  id: string
  label: string
  style: CellFormat
}

export type ConditionalFormatValueLabels = {
  firstValueLabel: string
  secondValueLabel: string
}

export type ConditionalFormatEditDraft = {
  kind: ConditionalFormatKind
  operator: string
  formula1: string
  formula2: string
  styleId: string
  customFillColor: string
  customTextColor: string
  customBold: boolean
  customItalic: boolean
  customUnderline: boolean
  customBorderStyle: string
  customBorderColor: string
  stopIfTrue: boolean
  rangeDraft: string
  dataBarColor: string
  dataBarNegativeColor: string
  dataBarBorderColor: string
  dataBarDirection: string
  dataBarShowValue: boolean
  scaleMinColor: string
  scaleMidColor: string
  scaleMaxColor: string
  iconSet: string
  iconSetShowValue: boolean
  iconSetReverse: boolean
  iconSetThresholdType: string
  iconSetThresholds: string
  visualMinType: string
  visualMaxType: string
  visualMidType: string
  visualMidValue: string
}

export type BuildConditionalFormatRuleArgs = ConditionalFormatEditDraft & {
  editingRule?: SpreadsheetConditionalFormatRule | null
  selectedRange: SpreadsheetRangeAddress
  sortedRules: SpreadsheetConditionalFormatRule[]
  customStyle: CellFormat
  idSalt: string
}

export type BuildConditionalFormatDialogApplyPlanArgs = Omit<BuildConditionalFormatRuleArgs, 'idSalt'> & {
  nowMs?: number
}

export type BuildConditionalFormatRuleResult =
  | { rule: SpreadsheetConditionalFormatRule; error: null }
  | { rule: null; error: string }

export type ConditionalFormatDialogApplyPlan =
  | { action: 'upsert-rule'; rule: SpreadsheetConditionalFormatRule }
  | { action: 'blocked'; error: string }

export type ConditionalFormatDialogClearPlan = {
  action: 'clear-selection-rules'
}

export type ConditionalFormatDialogReorderPlan =
  | { action: 'reorder-rules'; ruleIds: string[] }
  | { action: 'noop' }

export const CONDITIONAL_FORMAT_RULE_TYPES: ConditionalFormatRuleTypeOption[] = [
  { id: 'cellIs', label: 'Highlight Cell Rules' },
  { id: 'formula', label: 'Use a Formula' },
  { id: 'duplicateValues', label: 'Duplicate Values' },
  { id: 'timePeriod', label: 'Dates Occurring' },
  { id: 'topBottom', label: 'Top/Bottom Rules' },
  { id: 'aboveBelowAverage', label: 'Above/Below Average' },
  { id: 'colorScale', label: 'Color Scales' },
  { id: 'dataBar', label: 'Data Bars' },
  { id: 'iconSet', label: 'Icon Sets' },
]

export const CONDITIONAL_FORMAT_OPERATOR_OPTIONS_BY_KIND: Record<ConditionalFormatKind, ConditionalFormatOption[]> = {
  cellIs: [
    { value: 'greaterThan', label: 'Greater Than' },
    { value: 'lessThan', label: 'Less Than' },
    { value: 'greaterThanOrEqual', label: 'Greater Than or Equal To' },
    { value: 'lessThanOrEqual', label: 'Less Than or Equal To' },
    { value: 'between', label: 'Between' },
    { value: 'notBetween', label: 'Not Between' },
    { value: 'equal', label: 'Equal To' },
    { value: 'notEqual', label: 'Not Equal To' },
    { value: 'containsText', label: 'Text Contains' },
    { value: 'notContainsText', label: 'Text Does Not Contain' },
    { value: 'beginsWith', label: 'Text Begins With' },
    { value: 'notBeginsWith', label: 'Text Does Not Begin With' },
    { value: 'endsWith', label: 'Text Ends With' },
    { value: 'notEndsWith', label: 'Text Does Not End With' },
    { value: 'containsBlanks', label: 'Blanks' },
    { value: 'notContainsBlanks', label: 'No Blanks' },
    { value: 'containsErrors', label: 'Errors' },
    { value: 'notContainsErrors', label: 'No Errors' },
  ],
  formula: [{ value: 'formula', label: 'Formula evaluates to TRUE' }],
  duplicateValues: [
    { value: 'duplicate', label: 'Duplicate' },
    { value: 'unique', label: 'Unique' },
  ],
  timePeriod: [
    { value: 'yesterday', label: 'Yesterday' },
    { value: 'today', label: 'Today' },
    { value: 'tomorrow', label: 'Tomorrow' },
    { value: 'last7Days', label: 'In the Last 7 Days' },
    { value: 'lastWeek', label: 'Last Week' },
    { value: 'thisWeek', label: 'This Week' },
    { value: 'nextWeek', label: 'Next Week' },
    { value: 'lastMonth', label: 'Last Month' },
    { value: 'thisMonth', label: 'This Month' },
    { value: 'nextMonth', label: 'Next Month' },
  ],
  topBottom: [
    { value: 'top', label: 'Top' },
    { value: 'bottom', label: 'Bottom' },
    { value: 'topPercent', label: 'Top %' },
    { value: 'bottomPercent', label: 'Bottom %' },
  ],
  aboveBelowAverage: [
    { value: 'aboveAverage', label: 'Above Average' },
    { value: 'belowAverage', label: 'Below Average' },
    { value: 'equalOrAboveAverage', label: 'Equal or Above Average' },
    { value: 'equalOrBelowAverage', label: 'Equal or Below Average' },
    { value: 'above1StdDev', label: '1 Std Dev Above' },
    { value: 'below1StdDev', label: '1 Std Dev Below' },
    { value: 'above2StdDev', label: '2 Std Dev Above' },
    { value: 'below2StdDev', label: '2 Std Dev Below' },
    { value: 'above3StdDev', label: '3 Std Dev Above' },
    { value: 'below3StdDev', label: '3 Std Dev Below' },
  ],
  colorScale: [{ value: 'colorScale', label: 'Color scale' }],
  dataBar: [{ value: 'dataBar', label: 'Data bar' }],
  iconSet: [{ value: 'iconSet', label: 'Icon set' }],
}

export const CONDITIONAL_FORMAT_PRESET_STYLES: ConditionalFormatPresetStyle[] = [
  {
    id: 'light-red-fill',
    label: 'Light Red Fill',
    style: {
      backgroundColor: '#ffc7ce',
      textColor: '#9c0006',
      fill: { type: 'solid', foregroundColor: '#ffc7ce' },
      font: { color: '#9c0006' },
    },
  },
  {
    id: 'yellow-fill',
    label: 'Yellow Fill',
    style: {
      backgroundColor: '#ffeb9c',
      textColor: '#9c6500',
      fill: { type: 'solid', foregroundColor: '#ffeb9c' },
      font: { color: '#9c6500' },
    },
  },
  {
    id: 'green-fill',
    label: 'Green Fill',
    style: {
      backgroundColor: '#c6efce',
      textColor: '#006100',
      fill: { type: 'solid', foregroundColor: '#c6efce' },
      font: { color: '#006100' },
    },
  },
]

export const CONDITIONAL_FORMAT_VISUAL_THRESHOLD_TYPES: ConditionalFormatOption[] = [
  { value: 'lowestValue', label: 'Lowest value' },
  { value: 'highestValue', label: 'Highest value' },
  { value: 'number', label: 'Number' },
  { value: 'percent', label: 'Percent' },
  { value: 'percentile', label: 'Percentile' },
]

export const CONDITIONAL_FORMAT_BORDER_STYLE_OPTIONS: ConditionalFormatOption[] = [
  { value: 'none', label: 'None' },
  { value: 'thin', label: 'Thin' },
  { value: 'medium', label: 'Medium' },
  { value: 'thick', label: 'Thick' },
  { value: 'dashed', label: 'Dashed' },
  { value: 'dotted', label: 'Dotted' },
  { value: 'double', label: 'Double' },
]

export const CONDITIONAL_FORMAT_DATA_BAR_DIRECTION_OPTIONS: ConditionalFormatOption[] = [
  { value: 'leftToRight', label: 'Left-to-right' },
  { value: 'rightToLeft', label: 'Right-to-left' },
  { value: 'context', label: 'Context' },
]

export const CONDITIONAL_FORMAT_ICON_SET_OPTIONS: ConditionalFormatOption[] = [
  { value: '3Arrows', label: '3 Arrows' },
  { value: '4Arrows', label: '4 Arrows' },
  { value: '5Arrows', label: '5 Arrows' },
  { value: '3TrafficLights', label: '3 Traffic Lights' },
  { value: '4TrafficLights', label: '4 Traffic Lights' },
  { value: '3Symbols', label: '3 Symbols' },
  { value: '5Quarters', label: '5 Quarters' },
]

export const CONDITIONAL_FORMAT_ICON_THRESHOLD_TYPE_OPTIONS: ConditionalFormatOption[] = [
  { value: 'percent', label: 'Percent' },
  { value: 'number', label: 'Number' },
]

export const CONDITIONAL_FORMAT_RULE_FILTER_OPTIONS: Array<{
  value: ConditionalFormatRuleFilter
  label: string
}> = [
  { value: 'selection', label: 'Current Selection' },
  { value: 'worksheet', label: 'This Worksheet' },
  { value: 'workbook', label: 'Workbook' },
]

export function conditionalFormatStyleColor(
  style: CellFormat,
  section: 'fill' | 'font',
  fallback: string,
): string {
  const source = section === 'fill'
    ? (style.fill as Record<string, unknown> | undefined)
    : (style.font as Record<string, unknown> | undefined)
  const value = section === 'fill'
    ? style.backgroundColor ?? source?.foregroundColor ?? source?.backgroundColor
    : style.textColor ?? source?.color
  const text = String(value || '').trim()
  return /^#[0-9a-f]{6}$/i.test(text) ? text : fallback
}

export function buildCustomConditionalFormatStyle(args: {
  fillColor: string
  textColor: string
  bold: boolean
  italic: boolean
  underline: boolean
  borderStyle: string
  borderColor: string
}): CellFormat {
  const side = args.borderStyle === 'none'
    ? null
    : { style: args.borderStyle, color: args.borderColor }
  return {
    backgroundColor: args.fillColor,
    textColor: args.textColor,
    fill: { type: 'solid', foregroundColor: args.fillColor, backgroundColor: args.fillColor },
    font: {
      color: args.textColor,
      ...(args.bold ? { bold: true } : null),
      ...(args.italic ? { italic: true } : null),
      ...(args.underline ? { underline: 'single' } : null),
    },
    ...(args.bold ? { bold: true } : null),
    ...(args.italic ? { italic: true } : null),
    ...(args.underline ? { underline: 'single' } : null),
    ...(side ? {
      border: {
        top: side,
        right: side,
        bottom: side,
        left: side,
      },
    } : null),
  }
}

export function authoredConditionalFormatRuleKind(
  kind: ConditionalFormatKind,
  operator: string,
): AuthoredConditionalFormatKind {
  if (kind === 'duplicateValues') {
    return operator === 'unique' ? 'uniqueValues' : 'duplicateValues'
  }
  if (kind === 'cellIs') {
    if (operator === 'containsBlanks') return 'containsBlanks'
    if (operator === 'notContainsBlanks') return 'notContainsBlanks'
    if (operator === 'containsErrors') return 'containsErrors'
    if (operator === 'notContainsErrors') return 'notContainsErrors'
  }
  return kind
}

export function conditionalFormatDialogStateFromRule(
  rule: SpreadsheetConditionalFormatRule,
): { kind: ConditionalFormatKind; operator: string } {
  const semanticKind = String(rule.kind || 'cellIs')
  switch (semanticKind) {
    case 'uniqueValues':
      return { kind: 'duplicateValues', operator: 'unique' }
    case 'duplicateValues':
      return { kind: 'duplicateValues', operator: String(rule.operator || 'duplicate') }
    case 'containsBlanks':
    case 'notContainsBlanks':
    case 'containsErrors':
    case 'notContainsErrors':
      return { kind: 'cellIs', operator: semanticKind }
    case 'timePeriod':
      return { kind: 'timePeriod', operator: String(rule.operator || 'today') }
    case 'topBottom':
    case 'aboveBelowAverage':
    case 'colorScale':
    case 'dataBar':
    case 'iconSet':
    case 'formula':
    case 'cellIs':
      return {
        kind: semanticKind,
        operator: String(rule.operator || CONDITIONAL_FORMAT_OPERATOR_OPTIONS_BY_KIND[semanticKind]?.[0]?.value || 'greaterThan'),
      }
    default:
      return { kind: 'cellIs', operator: String(rule.operator || 'greaterThan') }
  }
}

export function conditionalFormatOperatorNeedsOperand(
  kind: ConditionalFormatKind,
  operator: string,
): boolean {
  return !(
    kind === 'duplicateValues'
    || kind === 'timePeriod'
    || kind === 'aboveBelowAverage'
    || kind === 'colorScale'
    || kind === 'dataBar'
    || kind === 'iconSet'
    || operator === 'containsBlanks'
    || operator === 'notContainsBlanks'
    || operator === 'containsErrors'
    || operator === 'notContainsErrors'
  )
}

export function buildConditionalFormatRuleId(
  kind: string,
  range: SpreadsheetRangeAddress,
  salt: string,
): string {
  return `cf_${kind}_${range.sheet_id}_${range.start_row}_${range.start_col}_${range.end_row}_${range.end_col}_${salt}`
    .replace(/[^a-z0-9_]+/gi, '_')
    .toLowerCase()
}

export function conditionalFormatRangesOverlap(
  left: SpreadsheetRangeAddress,
  right: SpreadsheetRangeAddress,
): boolean {
  return String(left.sheet_id).toLowerCase() === String(right.sheet_id).toLowerCase()
    && left.start_col <= right.end_col
    && left.end_col >= right.start_col
    && left.start_row <= right.end_row
    && left.end_row >= right.start_row
}

export function conditionalFormatRuleAppliesToSelection(
  rule: SpreadsheetConditionalFormatRule,
  selectedRange: SpreadsheetRangeAddress,
): boolean {
  return rule.ranges.some((range) => conditionalFormatRangesOverlap(range, selectedRange))
}

export function conditionalFormatRuleAppliesToSheet(
  rule: SpreadsheetConditionalFormatRule,
  sheetId: string,
): boolean {
  return rule.ranges.some((range) => String(range.sheet_id).toLowerCase() === sheetId.toLowerCase())
}

export function formatConditionalFormatRuleRange(range: SpreadsheetRangeAddress): string {
  return `${range.sheet_id}!${formatSelectionAddress({
    col: range.start_col,
    row: range.start_row,
    endCol: range.end_col,
    endRow: range.end_row,
  }, { colCount: 16_384, rowCount: 1_048_576 })}`
}

export function parseConditionalFormatRuleRangeDraft(
  input: string,
  fallbackSheetId: string,
): SpreadsheetRangeAddress[] | null {
  const text = String(input || '').trim().replace(/^=/, '')
  if (!text) return null
  const ranges: SpreadsheetRangeAddress[] = []
  for (const part of text.split(',')) {
    const token = part.trim()
    if (!token) return null
    const bangIndex = token.lastIndexOf('!')
    const sheetToken = bangIndex >= 0 ? token.slice(0, bangIndex).trim() : ''
    const addressToken = bangIndex >= 0 ? token.slice(bangIndex + 1).trim() : token
    const selection = parseA1Selection(addressToken)
    if (!selection) return null
    const sheetId = sheetToken
      ? sheetToken.replace(/^'/, '').replace(/'$/, '').replace(/''/g, "'")
      : fallbackSheetId
    ranges.push({
      sheet_id: sheetId,
      start_row: Math.min(selection.row, selection.endRow),
      start_col: Math.min(selection.col, selection.endCol),
      end_row: Math.max(selection.row, selection.endRow),
      end_col: Math.max(selection.col, selection.endCol),
    })
  }
  return ranges
}

export function inferConditionalFormatPresetStyleId(style?: Record<string, unknown>): string {
  const background = String(
    (style?.backgroundColor as string | undefined)
    || ((style?.fill as Record<string, unknown> | undefined)?.foregroundColor as string | undefined)
    || '',
  ).toLowerCase()
  const text = String(
    (style?.textColor as string | undefined)
    || ((style?.font as Record<string, unknown> | undefined)?.color as string | undefined)
    || '',
  ).toLowerCase()
  return CONDITIONAL_FORMAT_PRESET_STYLES.find((preset) => (
    String(preset.style.backgroundColor || '').toLowerCase() === background
    || String(preset.style.textColor || '').toLowerCase() === text
  ))?.id ?? CONDITIONAL_FORMAT_PRESET_STYLES[0].id
}

export function conditionalFormatIsVisualRule(kind: ConditionalFormatKind): boolean {
  return kind === 'dataBar' || kind === 'colorScale' || kind === 'iconSet'
}

export function buildConditionalFormatValueLabels(kind: ConditionalFormatKind): ConditionalFormatValueLabels {
  const visualRule = conditionalFormatIsVisualRule(kind)
  return {
    firstValueLabel: kind === 'formula'
      ? 'Formula'
      : visualRule
        ? 'Minimum'
        : 'Value or formula',
    secondValueLabel: visualRule ? 'Maximum' : 'Second value',
  }
}

export function sortConditionalFormatRules(
  rules: SpreadsheetConditionalFormatRule[],
): SpreadsheetConditionalFormatRule[] {
  return [...rules].sort((left, right) => (
    Number(left.priority ?? 0) - Number(right.priority ?? 0)
    || String(left.id).localeCompare(String(right.id))
  ))
}

export function filterConditionalFormatRules(args: {
  rules: SpreadsheetConditionalFormatRule[]
  filter: ConditionalFormatRuleFilter
  selectedRange: SpreadsheetRangeAddress
}): SpreadsheetConditionalFormatRule[] {
  return args.rules.filter((rule) => {
    if (args.filter === 'workbook') return true
    if (args.filter === 'worksheet') return conditionalFormatRuleAppliesToSheet(rule, args.selectedRange.sheet_id)
    return conditionalFormatRuleAppliesToSelection(rule, args.selectedRange)
  })
}

export function buildConditionalFormatEditDraft(args: {
  rule: SpreadsheetConditionalFormatRule
  fallbackStyle: CellFormat
}): ConditionalFormatEditDraft {
  const nextState = conditionalFormatDialogStateFromRule(args.rule)
  const style = (args.rule.style ?? args.rule.format ?? args.fallbackStyle) as CellFormat
  const font = style.font && typeof style.font === 'object' && !Array.isArray(style.font)
    ? style.font as Record<string, unknown>
    : {}
  const border = style.border && typeof style.border === 'object' && !Array.isArray(style.border)
    ? style.border as Record<string, unknown>
    : {}
  const borderSide = ['top', 'right', 'bottom', 'left']
    .map((key) => border[key])
    .find((side) => side && typeof side === 'object' && !Array.isArray(side)) as Record<string, unknown> | undefined
  const visual = (args.rule.style ?? args.rule.format ?? {}) as Record<string, unknown>
  const dataBar = visual.dataBar && typeof visual.dataBar === 'object' && !Array.isArray(visual.dataBar)
    ? visual.dataBar as Record<string, unknown>
    : {}
  const colorScale = visual.colorScale && typeof visual.colorScale === 'object' && !Array.isArray(visual.colorScale)
    ? visual.colorScale as Record<string, unknown>
    : {}
  const iconSetStyle = visual.iconSet && typeof visual.iconSet === 'object' && !Array.isArray(visual.iconSet)
    ? visual.iconSet as Record<string, unknown>
    : {}
  const visualStyle = nextState.kind === 'dataBar'
    ? dataBar
    : nextState.kind === 'colorScale'
      ? colorScale
      : nextState.kind === 'iconSet'
        ? iconSetStyle
        : {}

  return {
    kind: nextState.kind,
    operator: nextState.operator,
    formula1: String(args.rule.formula1 || ''),
    formula2: String(args.rule.formula2 || ''),
    styleId: inferConditionalFormatPresetStyleId(args.rule.style ?? args.rule.format),
    customFillColor: conditionalFormatStyleColor(style, 'fill', '#ffc7ce'),
    customTextColor: conditionalFormatStyleColor(style, 'font', '#9c0006'),
    customBold: style.bold === true || font.bold === true,
    customItalic: style.italic === true || font.italic === true,
    customUnderline: Boolean(style.underline ?? font.underline),
    customBorderStyle: String(borderSide?.style ?? 'none'),
    customBorderColor: conditionalFormatStyleColor({ textColor: borderSide?.color as string | undefined }, 'font', '#1f2937'),
    stopIfTrue: args.rule.stop_if_true === true,
    rangeDraft: args.rule.ranges.map(formatConditionalFormatRuleRange).join(', '),
    dataBarColor: String(dataBar.color ?? '#63c384'),
    dataBarNegativeColor: String(dataBar.negativeColor ?? '#f87171'),
    dataBarBorderColor: String(dataBar.borderColor ?? '#6aa84f'),
    dataBarDirection: String(dataBar.direction ?? 'leftToRight'),
    dataBarShowValue: dataBar.showValue !== false,
    scaleMinColor: String(colorScale.minColor ?? '#f8696b'),
    scaleMidColor: String(colorScale.midColor ?? '#ffeb84'),
    scaleMaxColor: String(colorScale.maxColor ?? '#63be7b'),
    iconSet: String(iconSetStyle.set ?? '3Arrows'),
    iconSetShowValue: iconSetStyle.showValue !== false,
    iconSetReverse: iconSetStyle.reverse === true,
    iconSetThresholdType: String(iconSetStyle.thresholdType ?? 'percent'),
    iconSetThresholds: Array.isArray(iconSetStyle.thresholds)
      ? iconSetStyle.thresholds.map((value) => String(value)).join(', ')
      : String(iconSetStyle.thresholds ?? ''),
    visualMinType: String(visualStyle.minType ?? 'lowestValue'),
    visualMaxType: String(visualStyle.maxType ?? 'highestValue'),
    visualMidType: String(visualStyle.midType ?? 'percentile'),
    visualMidValue: String(visualStyle.midValue ?? '50'),
  }
}

export function buildConditionalFormatVisualStyle(args: BuildConditionalFormatRuleArgs): CellFormat {
  const visualThresholds = conditionalFormatIsVisualRule(args.kind)
    ? {
      minType: args.visualMinType,
      maxType: args.visualMaxType,
      minValue: args.formula1.trim() || undefined,
      maxValue: args.formula2.trim() || undefined,
    }
    : {}
  const colorScaleThresholds = args.kind === 'colorScale'
    ? {
      ...visualThresholds,
      midType: args.visualMidType,
      midValue: args.visualMidValue.trim() || undefined,
    }
    : visualThresholds

  if (args.kind === 'dataBar') {
    return {
      dataBar: {
        color: args.dataBarColor,
        negativeColor: args.dataBarNegativeColor,
        borderColor: args.dataBarBorderColor,
        direction: args.dataBarDirection,
        showValue: args.dataBarShowValue,
        ...visualThresholds,
      },
    }
  }
  if (args.kind === 'colorScale') {
    return {
      colorScale: {
        minColor: args.scaleMinColor,
        midColor: args.scaleMidColor,
        maxColor: args.scaleMaxColor,
        ...colorScaleThresholds,
      },
    }
  }
  if (args.kind === 'iconSet') {
    return {
      iconSet: {
        set: args.iconSet,
        showValue: args.iconSetShowValue,
        reverse: args.iconSetReverse,
        thresholdType: args.iconSetThresholdType,
        thresholds: args.iconSetThresholds.split(',').map((value) => value.trim()).filter(Boolean),
        ...visualThresholds,
      },
    }
  }
  return args.customStyle
}

export function normalizeConditionalFormatProjectionStyle(
  style?: Record<string, unknown> | null,
): CellFormat | null {
  if (!style || typeof style !== 'object' || Array.isArray(style)) return null
  const normalized: CellFormat = { ...style }
  const fill = normalized.fill && typeof normalized.fill === 'object' && !Array.isArray(normalized.fill)
    ? { ...normalized.fill }
    : {}
  const font = normalized.font && typeof normalized.font === 'object' && !Array.isArray(normalized.font)
    ? { ...normalized.font }
    : {}
  const fillColor = typeof normalized.fillColor === 'string' && normalized.fillColor.trim()
    ? normalized.fillColor.trim()
    : ''
  const backgroundColor = typeof normalized.backgroundColor === 'string' && normalized.backgroundColor.trim()
    ? normalized.backgroundColor.trim()
    : ''
  const fontColor = typeof normalized.fontColor === 'string' && normalized.fontColor.trim()
    ? normalized.fontColor.trim()
    : ''
  const textColor = typeof normalized.textColor === 'string' && normalized.textColor.trim()
    ? normalized.textColor.trim()
    : ''

  if (!backgroundColor && fillColor) normalized.backgroundColor = fillColor
  if (!fill.foregroundColor && (backgroundColor || fillColor)) {
    fill.foregroundColor = backgroundColor || fillColor
  }
  if (!normalized.textColor && fontColor) normalized.textColor = fontColor
  if (!font.color && (textColor || fontColor)) {
    font.color = textColor || fontColor
  }
  if (Object.keys(fill).length > 0) normalized.fill = fill
  if (Object.keys(font).length > 0) normalized.font = font
  return normalized
}

export function mergeConditionalFormatProjectionStyles(
  styles: unknown,
): CellFormat | null {
  const entries = Array.isArray(styles) ? styles : [styles]
  let merged: CellFormat | null = null
  for (const entry of entries) {
    if (!entry || typeof entry !== 'object' || Array.isArray(entry)) continue
    const normalized = normalizeConditionalFormatProjectionStyle(entry as Record<string, unknown>)
    if (!normalized) continue
    merged = { ...(merged ?? {}), ...normalized }
  }
  return merged
}

export function conditionalFormatProjectionStyleFromMetadata(
  metadata: unknown,
): CellFormat | null {
  if (!metadata || typeof metadata !== 'object' || Array.isArray(metadata)) return null
  const record = metadata as Record<string, unknown>
  const stacked = mergeConditionalFormatProjectionStyles(record.conditional_formats)
  const final = mergeConditionalFormatProjectionStyles(record.conditional_format)
  if (!stacked && !final) return null
  return {
    ...(stacked ?? {}),
    ...(final ?? {}),
  }
}

export function buildConditionalFormatRulePayload(
  args: BuildConditionalFormatRuleArgs,
): BuildConditionalFormatRuleResult {
  const parsedRanges = parseConditionalFormatRuleRangeDraft(args.rangeDraft, args.selectedRange.sheet_id)
  if (!parsedRanges?.length) {
    return {
      rule: null,
      error: 'Enter one or more valid A1 ranges, such as A1:B10 or sheet_2!C:C.',
    }
  }
  const visualRule = conditionalFormatIsVisualRule(args.kind)
  const engineKind = authoredConditionalFormatRuleKind(args.kind, args.operator)
  const needsOperand = conditionalFormatOperatorNeedsOperand(args.kind, args.operator)
  return {
    error: null,
    rule: {
      id: args.editingRule?.id ?? buildConditionalFormatRuleId(engineKind, args.selectedRange, args.idSalt),
      priority: args.editingRule?.priority ?? (args.sortedRules.reduce((max, rule) => Math.max(max, Number(rule.priority ?? 0) || 0), 0) + 1),
      ranges: parsedRanges,
      stop_if_true: args.stopIfTrue,
      kind: engineKind,
      operator: args.operator,
      formula1: (needsOperand || visualRule) ? (args.formula1.trim() || null) : null,
      formula2: (needsOperand || visualRule) ? (args.formula2.trim() || null) : null,
      style: buildConditionalFormatVisualStyle(args),
    },
  }
}

export function buildConditionalFormatDialogApplyPlan(
  args: BuildConditionalFormatDialogApplyPlanArgs,
): ConditionalFormatDialogApplyPlan {
  const result = buildConditionalFormatRulePayload({
    ...args,
    idSalt: Math.floor(Number(args.nowMs ?? Date.now())).toString(36),
  })
  return result.rule
    ? { action: 'upsert-rule', rule: result.rule }
    : { action: 'blocked', error: result.error }
}

export function buildConditionalFormatDialogClearPlan(): ConditionalFormatDialogClearPlan {
  return { action: 'clear-selection-rules' }
}

export function buildConditionalFormatDialogReorderPlan(args: {
  ruleId: string
  direction: -1 | 1
  visibleRules: SpreadsheetConditionalFormatRule[]
  sortedRules: SpreadsheetConditionalFormatRule[]
}): ConditionalFormatDialogReorderPlan {
  const index = args.visibleRules.findIndex((rule) => rule.id === args.ruleId)
  const nextIndex = index + args.direction
  if (index < 0 || nextIndex < 0 || nextIndex >= args.visibleRules.length) {
    return { action: 'noop' }
  }
  const targetId = args.visibleRules[nextIndex]?.id
  if (!targetId) return { action: 'noop' }
  const nextRules = [...args.sortedRules]
  const ruleIndex = nextRules.findIndex((entry) => entry.id === args.ruleId)
  if (ruleIndex < 0) return { action: 'noop' }
  const [rule] = nextRules.splice(ruleIndex, 1)
  const targetIndex = nextRules.findIndex((entry) => entry.id === targetId)
  if (!rule || targetIndex < 0) return { action: 'noop' }
  nextRules.splice(args.direction > 0 ? targetIndex + 1 : targetIndex, 0, rule)
  return { action: 'reorder-rules', ruleIds: nextRules.map((entry) => entry.id) }
}
